import { useCallback, useEffect, useMemo, useRef, useState } from "react"

type ItemId = number | string

const NO_NEW_IDS: ReadonlySet<ItemId> = new Set()

const ACKNOWLEDGED_HIGHLIGHT_MS = 4000

export type NewItems = Readonly<{
    newIds: ReadonlySet<ItemId>
    highlightedIds: ReadonlySet<ItemId>
    acknowledge: () => void
}>

export function useNewItems<TItem>(
    data: ReadonlyArray<TItem> | undefined,
    getId: (item: TItem) => ItemId,
    resetKey: unknown,
): NewItems {
    const savedGetId = useRef(getId)

    useEffect(() => {
        savedGetId.current = getId
    }, [getId])

    const ids = useMemo(() => data?.map((item) => savedGetId.current(item)), [data])
    const [seenIds, setSeenIds] = useState<ReadonlySet<ItemId>>()
    const [acknowledgedIds, setAcknowledgedIds] = useState<ReadonlySet<ItemId>>(NO_NEW_IDS)
    const serializedResetKey = JSON.stringify(resetKey ?? null)

    // Cached query keys resolve without ever emptying `data`, so the baseline needs its own reset signal.
    useEffect(() => {
        setSeenIds(undefined)
        setAcknowledgedIds(NO_NEW_IDS)
    }, [serializedResetKey])

    useEffect(() => {
        if (ids === undefined) {
            setSeenIds(undefined)
            return
        }
        setSeenIds((previous) => previous ?? new Set(ids))
    }, [ids])

    useEffect(() => {
        if (acknowledgedIds.size === 0) return
        const timeout = setTimeout(() => setAcknowledgedIds(NO_NEW_IDS), ACKNOWLEDGED_HIGHLIGHT_MS)
        return () => clearTimeout(timeout)
    }, [acknowledgedIds])

    const newIds = useMemo(() => {
        if (ids === undefined || seenIds === undefined) return NO_NEW_IDS
        return new Set(ids.filter((id) => !seenIds.has(id)))
    }, [ids, seenIds])

    const highlightedIds = useMemo(
        () => (acknowledgedIds.size === 0 ? newIds : new Set(Array.from(newIds).concat(Array.from(acknowledgedIds)))),
        [newIds, acknowledgedIds],
    )

    const acknowledge = useCallback(() => {
        setAcknowledgedIds(newIds)
        setSeenIds(new Set(ids ?? []))
    }, [ids, newIds])

    return { newIds, highlightedIds, acknowledge }
}
